import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const TokenManager: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Get JWT token from localStorage on mount
  React.useEffect(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || localStorage.getItem('token') || '';
    if (token) {
      setJwtToken(token);
    }
  }, []);

  // Validate Token state
  const [validateToken, setValidateToken] = useState('');
  const [validateResult, setValidateResult] = useState<any>(null);

  // Test Search state
  const [testApiToken, setTestApiToken] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [testLimit, setTestLimit] = useState(10);
  const [testResults, setTestResults] = useState<any>(null);

  // Create Token state
  const [tokenName, setTokenName] = useState('test-token');
  const [userGroupId, setUserGroupId] = useState('44aa4408-64d4-4396-bb19-794f3851d23f');
  const [jwtToken, setJwtToken] = useState(() => {
    // Initialize JWT from localStorage if available
    return localStorage.getItem('authToken') || localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  });
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [createdToken, setCreatedToken] = useState<any>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setMessage(null);
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showMessage('success', '✅ Copied to clipboard!');
  };

  // Validate Token
  const handleValidateToken = async () => {
    if (!validateToken.trim()) {
      showMessage('error', '⚠️ Please enter a token');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/tokens/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: validateToken }),
      });

      const data = await response.json();

      if (!response.ok || !data.valid) {
        showMessage('error', `❌ ${data.message || 'Token is invalid'}`);
        setValidateResult(null);
      } else {
        showMessage('success', '✅ Token is valid!');
        setValidateResult(data);
      }
    } catch (error: any) {
      showMessage('error', `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test Search
  const handleTestSearch = async () => {
    if (!testApiToken.trim() || !testQuery.trim()) {
      showMessage('error', '⚠️ Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/search/semantic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': testApiToken,
        },
        body: JSON.stringify({
          text: testQuery,
          limit: testLimit,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showMessage('error', `Search failed: ${data.message || response.statusText}`);
        setTestResults(null);
      } else {
        showMessage('success', `✅ Found ${data.results?.length || 0} results`);
        setTestResults(data.results);
      }
    } catch (error: any) {
      showMessage('error', `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create Token
  const handleCreateToken = async () => {
    if (!tokenName.trim() || !userGroupId.trim() || !jwtToken.trim()) {
      showMessage('error', '⚠️ Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          name: tokenName,
          userGroupId,
          expiresInDays: expiresInDays || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showMessage('error', `Failed to create token: ${data.message}`);
        setCreatedToken(null);
      } else {
        showMessage('success', '✅ Token created successfully!');
        setCreatedToken(data);
        setTokenName('');
        setJwtToken('');
      }
    } catch (error: any) {
      showMessage('error', `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader title="🔐 API Token Manager" subheader="No authentication required" />
        <CardContent>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Validate Token" id="tab-0" />
            <Tab label="Test Search" id="tab-1" />
            <Tab label="Create Token" id="tab-2" />
          </Tabs>

          {/* Validate Token Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="API Token (64-character hex string)"
                multiline
                rows={3}
                value={validateToken}
                onChange={(e) => setValidateToken(e.target.value)}
                placeholder="Paste the API token here..."
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleValidateToken}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                Validate Token
              </Button>

              {validateResult && (
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    ✅ Token Valid
                  </Typography>
                  <Typography variant="body2">
                    <strong>User Group ID:</strong> {validateResult.userGroupId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Token Name:</strong> {validateResult.tokenName}
                  </Typography>
                </Paper>
              )}
            </Box>
          </TabPanel>

          {/* Test Search Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="API Token"
                multiline
                rows={2}
                value={testApiToken}
                onChange={(e) => setTestApiToken(e.target.value)}
                placeholder="Paste the API token here..."
                fullWidth
              />
              <TextField
                label="Search Query"
                value={testQuery}
                onChange={(e) => setTestQuery(e.target.value)}
                placeholder="e.g., speed regulations"
                fullWidth
              />
              <TextField
                label="Limit Results"
                type="number"
                value={testLimit}
                onChange={(e) => setTestLimit(parseInt(e.target.value) || 10)}
                inputProps={{ min: 1, max: 100 }}
              />
              <Button
                variant="contained"
                onClick={handleTestSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                Test Search
              </Button>

              {testResults && (
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    📊 Search Results ({testResults.length} total)
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ fontSize: '11px', margin: 0 }}>
                      {JSON.stringify(testResults.slice(0, 3), null, 2)}
                    </pre>
                  </Box>
                  {testResults.length > 3 && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Showing first 3 of {testResults.length} results
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          </TabPanel>

          {/* Create Token Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info">
                To create a token, you need a valid JWT token from your authenticated session.
              </Alert>

              <TextField
                label="Token Name"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder="e.g., mcp-semantic-search"
                fullWidth
              />

              <TextField
                label="User Group ID (UUID)"
                value={userGroupId}
                onChange={(e) => setUserGroupId(e.target.value)}
                placeholder="e.g., 44aa4408-64d4-4396-bb19-794f3851d23f"
                fullWidth
                helperText="Use: 44aa4408-64d4-4396-bb19-794f3851d23f for Kontrol group"
              />

              <TextField
                label="JWT Token (for authentication)"
                multiline
                rows={3}
                value={jwtToken}
                onChange={(e) => setJwtToken(e.target.value)}
                placeholder="Paste your JWT token here..."
                fullWidth
              />

              <TextField
                label="Expires in (days)"
                type="number"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 7)}
                placeholder="Leave empty for no expiration"
                inputProps={{ min: 1 }}
              />

              <Button
                variant="contained"
                onClick={handleCreateToken}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                Create Token
              </Button>

              {createdToken && (
                <Paper sx={{ p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    ⚠️ Save this token - it will never be shown again!
                  </Alert>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {createdToken.token}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => copyToClipboard(createdToken.token)}
                    sx={{ mt: 1 }}
                  >
                    📋 Copy Token
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Expires: {new Date(createdToken.expiresAt).toLocaleString()}
                  </Typography>
                </Paper>
              )}
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TokenManager;
